package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.layout.AppHorizontalScrollbar
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.CostCategoryCost
import br.com.vvrefeicoes.portal.domain.model.CostsSnapshot
import br.com.vvrefeicoes.portal.domain.model.MonthlyCost
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalPeriod
import com.composables.icons.lucide.Banknote
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Scale
import com.composables.icons.lucide.TrendingDown
import com.composables.icons.lucide.Users
import io.github.koalaplot.core.bar.HorizontalBarPlot
import io.github.koalaplot.core.bar.VerticalBarPlot
import io.github.koalaplot.core.line.LinePlot
import io.github.koalaplot.core.style.LineStyle
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.Point
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_costs_axis
import portalvv.shared.generated.resources.operational_costs_col_category
import portalvv.shared.generated.resources.operational_costs_col_share
import portalvv.shared.generated.resources.operational_costs_col_total
import portalvv.shared.generated.resources.operational_costs_employees
import portalvv.shared.generated.resources.operational_costs_empty
import portalvv.shared.generated.resources.operational_costs_evolution_title
import portalvv.shared.generated.resources.operational_costs_fopag
import portalvv.shared.generated.resources.operational_costs_fopag_per_employee
import portalvv.shared.generated.resources.operational_costs_legend_cost
import portalvv.shared.generated.resources.operational_costs_legend_revenue
import portalvv.shared.generated.resources.operational_costs_operational
import portalvv.shared.generated.resources.operational_costs_share_revenue
import portalvv.shared.generated.resources.operational_costs_share_title
import portalvv.shared.generated.resources.operational_costs_share_total
import portalvv.shared.generated.resources.operational_costs_table_title
import portalvv.shared.generated.resources.operational_costs_top_title
import portalvv.shared.generated.resources.operational_costs_total
import kotlin.math.abs
import kotlin.math.round
import kotlin.math.roundToLong

private val COST_COLORS =
    listOf(
        AppPalette.Primary500,
        AppPalette.Accent500,
        AppPalette.Info500,
        AppPalette.Warning500,
        AppPalette.Success500,
        AppPalette.Error500,
        AppPalette.Primary900,
        AppPalette.Accent900,
        AppPalette.Neutral500,
    )

private val KPI_HEIGHT = 96.dp
private val CHART_HEIGHT = 300.dp

private const val W_CATEGORY = 2.6f
private const val W_MONTH = 1.4f

/** Largura mínima de uma coluna de mês na tabela, para caber o valor completo sem ellipsis. */
private val MONTH_COLUMN_MIN_WIDTH = 128.dp

@Composable
fun CostsContent() {
    val controller = rememberCostsController()
    val filters = LocalGlobalFilters.current
    val period = LocalGlobalPeriod.current

    LaunchedEffect(controller) {
        controller.init()
    }

    LaunchedEffect(period.selection) {
        controller.applyPeriod(period.selection)
    }

    LaunchedEffect(filters.unit, filters.manager, filters.director, filters.units) {
        controller.applyFilter(filters.selectedUnit, filters.selectedManager, filters.selectedDirector, filters.units)
    }

    val snapshot = controller.snapshot
    when {
        controller.loading ->
            Box(
                modifier = Modifier.fillMaxWidth().height(200.dp),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        snapshot == null ->
            EmptyState(message = stringResource(Res.string.operational_costs_empty))
        else -> {
            CostsKpis(snapshot)
            CostsEvolutionCard(snapshot.monthly)
            CostsCategoriesSection(snapshot)
            CostsTableCard(snapshot)
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun CostsKpis(snapshot: CostsSnapshot) {
    val revenue = snapshot.revenueCents
    val totalShareOfRevenue = if (revenue == 0L) 0.0 else snapshot.totalCents.toDouble() / revenue * 100
    val fopagShareOfRevenue = if (revenue == 0L) 0.0 else snapshot.fopagCents.toDouble() / revenue * 100
    val fopagShareOfTotal = if (snapshot.totalCents == 0L) 0.0 else snapshot.fopagCents.toDouble() / snapshot.totalCents * 100
    val operationalShareOfTotal = if (snapshot.totalCents == 0L) 0.0 else snapshot.operationalCents.toDouble() / snapshot.totalCents * 100
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        maxItemsInEachRow = 4,
    ) {
        CostKpiTile(
            icon = Lucide.Banknote,
            iconColor = AppPalette.Error500,
            iconContainerColor = AppPalette.Error100,
            label = stringResource(Res.string.operational_costs_total),
            value = money(snapshot.totalCents),
            secondary = stringResource(Res.string.operational_costs_share_revenue, percentText(totalShareOfRevenue)),
        )
        CostKpiTile(
            icon = Lucide.Users,
            iconColor = AppPalette.Primary500,
            iconContainerColor = AppPalette.Primary100,
            label = stringResource(Res.string.operational_costs_fopag),
            value = money(snapshot.fopagCents),
            secondary = "${stringResource(Res.string.operational_costs_share_total, percentText(fopagShareOfTotal))} · ${stringResource(Res.string.operational_costs_share_revenue, percentText(fopagShareOfRevenue))}",
        )
        CostKpiTile(
            icon = Lucide.TrendingDown,
            iconColor = AppPalette.Warning500,
            iconContainerColor = AppPalette.Warning100,
            label = stringResource(Res.string.operational_costs_operational),
            value = money(snapshot.operationalCents),
            secondary = stringResource(Res.string.operational_costs_share_total, percentText(operationalShareOfTotal)),
        )
        CostKpiTile(
            icon = Lucide.Scale,
            iconColor = AppPalette.Info500,
            iconContainerColor = AppPalette.Info100,
            label = stringResource(Res.string.operational_costs_fopag_per_employee),
            value = money(snapshot.fopagPerEmployeeCents),
            secondary = stringResource(Res.string.operational_costs_employees, snapshot.headcount),
        )
    }
}

@Composable
private fun FlowRowScope.CostKpiTile(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
    secondary: String,
) {
    Surface(
        modifier = Modifier.weight(1f).height(KPI_HEIGHT),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Surface(
                shape = AppShapes.small,
                color = iconContainerColor,
                modifier = Modifier.size(32.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = iconColor,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = secondary,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun CostsEvolutionCard(monthly: List<MonthlyCost>) {
    ChartCard(
        title = stringResource(Res.string.operational_costs_evolution_title),
        modifier = Modifier.fillMaxWidth(),
    ) {
        SeriesLegend(
            entries =
                listOf(
                    LegendEntry(AppPalette.Error500, stringResource(Res.string.operational_costs_legend_cost)),
                    LegendEntry(AppPalette.Success500, stringResource(Res.string.operational_costs_legend_revenue)),
                ),
        )
        if (monthly.isEmpty()) {
            Text(
                text = "—",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            val labels = monthly.map { MonthCalendar.shortLabel(it.period) }
            val maxValue = monthly.maxOf { maxOf(it.costCents, it.revenueCents) }.coerceAtLeast(1L).toFloat() / 100f
            val costValues = monthly.map { it.costCents.toFloat() / 100f }
            val revenuePoints = monthly.mapIndexed { index, item -> Point(labels[index], item.revenueCents.toFloat() / 100f) }
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = CategoryAxisModel(labels),
                yAxisModel = rememberValueAxis(maxValue, headroom = 1.3f),
                xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                yAxisContent =
                    rememberAxisContent(
                        title = { Text(stringResource(Res.string.operational_costs_axis), style = MaterialTheme.typography.labelSmall) },
                        labels = { value -> Text(compactMoney((value * 100).roundToLong()), style = MaterialTheme.typography.labelSmall) },
                    ),
                modifier = Modifier.fillMaxWidth().height(CHART_HEIGHT),
            ) {
                val costLabel = stringResource(Res.string.operational_costs_legend_cost)
                VerticalBarPlot(
                    xData = labels,
                    yData = costValues,
                    bar = { index, _, _ ->
                        val item = monthly[index]
                        HoverTooltip(
                            text =
                                buildString {
                                    append(labels[index])
                                    append('\n')
                                    append(costLabel)
                                    append(": ")
                                    append(money(item.costCents))
                                },
                        ) {
                            ValueBar(
                                color = AppPalette.Error500,
                                label = compactMoney(item.costCents),
                                horizontal = false,
                            )
                        }
                    },
                )
                val revenueLabel = stringResource(Res.string.operational_costs_legend_revenue)
                LinePlot(
                    data = revenuePoints,
                    lineStyle = LineStyle(brush = SolidColor(AppPalette.Success500), strokeWidth = 2.dp),
                    symbol = { point ->
                        val item = monthly.getOrNull(labels.indexOf(point.x))
                        HoverTooltip(
                            text =
                                buildString {
                                    append(point.x)
                                    append('\n')
                                    append(revenueLabel)
                                    append(": ")
                                    append(money(item?.revenueCents ?: 0L))
                                },
                        ) {
                            LinePointLabel(compactMoney((point.y * 100).roundToLong()), AppPalette.Success500)
                        }
                    },
                )
            }
        }
    }
}

@Composable
private fun CostsCategoriesSection(snapshot: CostsSnapshot) {
    if (snapshot.categories.isEmpty()) return
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        if (maxWidth >= 900.dp) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
            ) {
                TopCategoriesCard(snapshot.categories, Modifier.weight(1.3f))
                ShareCard(snapshot.categories, Modifier.weight(1f))
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                TopCategoriesCard(snapshot.categories, Modifier.fillMaxWidth())
                ShareCard(snapshot.categories, Modifier.fillMaxWidth())
            }
        }
    }
}

@Composable
private fun TopCategoriesCard(
    categories: List<CostCategoryCost>,
    modifier: Modifier,
) {
    val top = categories.take(5)
    val total = categories.sumOf { it.totalCents }.coerceAtLeast(1L)
    ChartCard(
        title = stringResource(Res.string.operational_costs_top_title),
        modifier = modifier,
    ) {
        val names = top.reversed().map { stringResource(it.title) }
        val values = top.reversed().map { it.totalCents.toFloat() / 100f }
        XYGraph(
            gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
            xAxisModel = rememberValueAxis(values.maxOrNull() ?: 0f, headroom = 1.2f),
            yAxisModel = CategoryAxisModel(names),
            xAxisContent = rememberAxisContent(labels = { value -> Text(money((value * 100).roundToLong()), style = MaterialTheme.typography.labelSmall) }),
            yAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
            modifier = Modifier.fillMaxWidth().height((top.size * 36 + 48).dp),
        ) {
            HorizontalBarPlot(
                xData = values,
                yData = names,
                barWidth = 0.6f,
                bar = { index, _, value ->
                    val category = top.reversed().getOrNull(index)
                    val share = if (category == null) 0.0 else category.totalCents.toDouble() / total * 100
                    HoverTooltip(text = "${value.y} — ${money(category?.totalCents ?: 0L)} (${percentText(share)})") {
                        ValueBar(
                            color = COST_COLORS[index % COST_COLORS.size],
                            label = compactMoney(category?.totalCents ?: 0L),
                            horizontal = true,
                        )
                    }
                },
            )
        }
    }
}

@Composable
private fun ShareCard(
    categories: List<CostCategoryCost>,
    modifier: Modifier,
) {
    val total = categories.sumOf { it.totalCents }.coerceAtLeast(1L)
    ChartCard(
        title = stringResource(Res.string.operational_costs_share_title),
        modifier = modifier,
    ) {
        DonutChart(
            slices =
                categories.mapIndexed { index, category ->
                    DonutSlice(stringResource(category.title), category.totalCents, COST_COLORS[index % COST_COLORS.size])
                },
            modifier = Modifier.align(Alignment.CenterHorizontally).size(200.dp),
        )
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            categories.forEachIndexed { index, category ->
                val share = category.totalCents.toDouble() / total * 100
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                ) {
                    Box(modifier = Modifier.size(12.dp).background(COST_COLORS[index % COST_COLORS.size], AppShapes.small))
                    Text(
                        text = stringResource(category.title),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    Text(
                        text = "${money(category.totalCents)} (${percentText(share)})",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
    }
}

@Composable
private fun CostsTableCard(snapshot: CostsSnapshot) {
    if (snapshot.categories.isEmpty() || snapshot.months.isEmpty()) return
    val scrollState = rememberScrollState()
    val total = snapshot.categoriesTotalCents.coerceAtLeast(1L)
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
            Text(
                text = stringResource(Res.string.operational_costs_table_title),
                style = MaterialTheme.typography.titleMedium,
            )
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val columnWeight = W_CATEGORY + (snapshot.months.size + 2) * W_MONTH
                val minTableWidth = MONTH_COLUMN_MIN_WIDTH * columnWeight / W_MONTH
                val tableWidth = maxOf(maxWidth, minTableWidth)
                Box(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
                    Column(modifier = Modifier.width(tableWidth)) {
                        Row(modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space8), verticalAlignment = Alignment.CenterVertically) {
                            HeaderCell(stringResource(Res.string.operational_costs_col_category), W_CATEGORY, TextAlign.Start)
                            snapshot.months.forEach { period ->
                                HeaderCell(MonthCalendar.shortLabel(period), W_MONTH, TextAlign.End)
                            }
                            HeaderCell(stringResource(Res.string.operational_costs_col_total), W_MONTH, TextAlign.End)
                            HeaderCell(stringResource(Res.string.operational_costs_col_share), W_MONTH, TextAlign.End)
                        }
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        snapshot.categories.forEachIndexed { index, category ->
                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12), verticalAlignment = Alignment.CenterVertically) {
                                BodyCell(stringResource(category.title), W_CATEGORY, TextAlign.Start)
                                category.monthlyCents.forEach { value ->
                                    BodyCell(money(value), W_MONTH, TextAlign.End)
                                }
                                BodyCell(money(category.totalCents), W_MONTH, TextAlign.End, bold = true)
                                BodyCell(percentText(category.totalCents.toDouble() / total * 100), W_MONTH, TextAlign.End)
                            }
                            if (index < snapshot.categories.lastIndex) {
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                            }
                        }
                    }
                }
            }
            AppHorizontalScrollbar(
                state = scrollState,
                modifier = Modifier.fillMaxWidth().padding(top = AppSpacing.space4),
            )
        }
    }
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
    align: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = align,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun RowScope.BodyCell(
    text: String,
    weight: Float,
    align: TextAlign,
    bold: Boolean = false,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        fontWeight = if (bold) FontWeight.SemiBold else FontWeight.Normal,
        color = MaterialTheme.colorScheme.onSurface,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = align,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

private fun money(cents: Long): String = "R$ ${MoneyFormatter.formatCents(cents)}"

private fun percentText(value: Double): String {
    val scaled = round(value * 10).toLong()
    return "${scaled / 10},${abs(scaled % 10)}%"
}
